import { z } from "zod";
import { ROLES } from "./rbac";
import { DESCRIPTION_LANGS } from "./description";
import { MATERIAL_SIZE_UNITS } from "./productMaterial";
import { ZH_LABEL_LISTS } from "./optionLabels";
import { FX_CURRENCIES, FX_PAIRS, normalizeRate } from "./fx";
import { P_PROCESS_PHOTO_SLOTS } from "./pProcess";

/** Consistent API error envelope: { error: { code, message, fields? } }. */
export interface ApiError {
  error: {
    code: string;
    message: string;
    fields?: Record<string, string>;
  };
}

/**
 * Người dùng TỰ đổi mật khẩu.
 *
 * Toàn bộ tài khoản chuyển đổi từ hệ thống cũ đang dùng CHUNG một mật khẩu tạm,
 * mà không có màn nào để đổi — nghĩa là ai biết mật khẩu đó thì vào được mọi
 * tài khoản. Đây là lỗ hổng thật, không phải chuyện tiện nghi.
 *
 * Đòi mật khẩu HIỆN TẠI: phiên bị chiếm (máy bỏ quên, cookie bị lấy) không được
 * biến thành chiếm luôn tài khoản.
 */
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: z.string().min(8).max(200),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/**
 * Ô đăng nhập nhận EMAIL của nhân viên HOẶC MÃ NHÀ CUNG CẤP.
 *
 * Khách chốt 15/09/2026: nhà cung cấp đăng nhập bằng mã ta cấp, không phải
 * email — nhiều xưởng nhỏ không có email riêng, hoặc dùng chung một hộp thư.
 *
 * Một ô, một biểu mẫu, một đường xác thực. Dựng trang đăng nhập thứ hai cho
 * nhà cung cấp là dựng chỗ thứ hai có thể lệch về quy tắc phiên, khoá tài
 * khoản và giới hạn số lần nhập sai.
 *
 * Trường tên là `identifier` chứ không phải `email`: nó nhận cả hai loại, và
 * gọi nó là `email` thì tên trường nói dối về thứ nó chứa. Kiểm định dạng nới
 * ra ở đây, nhưng KHÔNG nới gì về bảo mật — `auth.ts` vẫn phải tra ra đúng một
 * tài khoản và vẫn phải khớp mật khẩu.
 */
export const loginSchema = z
  .object({
    identifier: z.string().trim().min(1).max(200).optional(),
    /**
     * TÊN CŨ của cùng một ô, giữ lại cho bản SPA đang nằm trong trình duyệt.
     *
     * Đổi tên trường ngày 15/09/2026. Một người đang mở sẵn trang đăng nhập
     * với bản dựng cũ sẽ gửi `email`; nếu máy chủ chỉ nhận `identifier` thì họ
     * gõ đúng mật khẩu vẫn bị từ chối, và thông báo lại là "dữ liệu không hợp
     * lệ" chứ không phải "hãy tải lại trang". Trang đăng nhập là chỗ KHÔNG
     * được phép có cửa sập kiểu đó: người dùng chưa đăng nhập thì cũng chưa
     * nhận được thông báo có bản mới.
     *
     * Nhận cả hai không nới lỏng gì: hai tên cùng đi vào một chỗ, rồi qua đúng
     * một đường xác thực. Bỏ được khi chắc không còn bản cũ nào trong máy ai.
     */
    email: z.string().trim().min(1).max(200).optional(),
    password: z.string().min(6),
  })
  .transform((v) => ({ password: v.password, identifier: (v.identifier ?? v.email ?? "").trim() }))
  .refine((v) => v.identifier.length >= 3, { path: ["identifier"] });
export type LoginInput = z.infer<typeof loginSchema>;

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  q: z.string().trim().optional(),
  sort: z.string().optional(),
});
export type PaginationInput = z.infer<typeof paginationSchema>;

/**
 * Số điện thoại: CHUẨN HOÁ chứ không chặn.
 *
 * Hệ cũ có regex chặn, nhưng nó loại luôn số có máy lẻ ("ext" / "转") và ký tự
 * tiếng Trung mà nhà cung cấp Trung Quốc hay ghi kèm — tức là từ chối đúng
 * những số có thật. Ở đây chỉ cắt khoảng trắng thừa và gộp khoảng trắng liên
 * tiếp, để "  +86  138  0013 8000 " và "+86 138 0013 8000" không thành hai giá
 * trị khác nhau khi tra cứu.
 */
const phone = (max: number) =>
  z.string().max(max).transform((v) => v.trim().replace(/\s+/g, " ")).optional();

export const customerContactSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone1: phone(60),
  phone2: phone(60),
  email: z.string().email().max(160).optional().or(z.literal("")),
  wechat: z.string().max(80).optional(),
  section: z.string().max(120).optional(),
  brandName: z.string().max(120).optional(),
  team: z.string().max(120).optional(),
  sortOrder: z.number().int().min(0).optional(),
  /** Liên hệ riêng tư — chỉ quản trị viên gửi `true` được (máy chủ từ chối người khác). */
  isPrivate: z.boolean().optional(),
});
export type CustomerContactInput = z.infer<typeof customerContactSchema>;

export const customerCreateSchema = z.object({
  companyName: z.string().trim().min(2).max(200),
  /**
   * Mã khách / tên viết tắt (yêu cầu v1.8 mục 2.2) — nhận diện khách trong nội bộ. Rỗng = chưa có mã (null). Đã nhập thì duy nhất,
   * không phân biệt hoa thường (409 `customer_code_taken`). Định dạng tự do tới 30 ký tự cho tới khi khách chốt quy tắc.
   */
  code: z.string().trim().max(30).nullable().optional(),
  phone: phone(60),
  address: z.string().max(500).optional(),
  paymentTerm: z.string().max(120).optional(),
  /**
   * Bỏ trống thì máy chủ lấy theo khu vực dữ liệu của người tạo, người không bị giới hạn khu vực thì "vietnam" như trước
   * (29/09/2026). Trước đây zod tự điền "vietnam", nên người của đội Trung Quốc tạo khách mà không khai là mất khách đó khỏi
   * chính màn hình của mình.
   */
  classification: z.enum(["vietnam", "china"]).optional(),
  /**
   * Default only. Seeds proforma_invoices.p_process when a PI is created; it
   * never reaches back into PIs that already exist.
   */
  pProcessDefault: z.boolean().optional(),
  /**
   * Người theo đơn (跟单员, Roary 29/09/2026) — nhiều người, ghi đè cả danh sách khi gửi. Vắng: bản sửa để nguyên danh sách;
   * bản tạo lấy người tạo (trừ quản trị viên — khách chưa có người theo đơn thì chỉ quản trị viên và tài khoản xem tất cả khách
   * thấy). Thay ô một người `customerServiceUserId` cũ; tab mở từ trước còn gửi ô đó thì zod bỏ đi, danh sách không bị đụng.
   */
  followerUserIds: z.array(z.string().min(1).max(64)).max(20).optional(),
  /**
   * Khách riêng tư (Roary 30/09/2026): chỉ quản trị viên thấy khách và chứng từ của khách. Chỉ quản trị viên gửi được; vắng =
   * để nguyên (bản tạo: không riêng tư).
   */
  isPrivate: z.boolean().optional(),
  /**
   * Ít nhất MỘT người liên hệ khi gửi lên, như hệ cũ bắt buộc.
   *
   * Chứng từ gửi khách (PI, hoá đơn, phiếu giao hàng) đều in khối liên hệ; khách
   * không có ai thì ô đó trống và bên nhận không biết hỏi ai. 58/58 khách trên
   * production đều đã có liên hệ, nên quy tắc này không khoá bản ghi nào.
   *
   * Vẫn `optional()`: undefined = để nguyên danh sách hiện có (bản sửa chỉ đổi
   * một ô khác không phải gửi lại cả danh bạ). Trần 15 theo legacy MAX_CONTACTS.
   *
   * Người không phải quản trị viên gửi danh sách thì chỉ thay những liên hệ họ
   * THẤY; liên hệ riêng tư (30/09/2026) máy chủ giữ nguyên.
   */
  contacts: z.array(customerContactSchema).min(1).max(15).optional(),
});
export type CustomerCreateInput = z.infer<typeof customerCreateSchema>;

export const customerUpdateSchema = customerCreateSchema.partial();

/* ── Yêu cầu v1.8 (11/10/2026): tệp bằng chứng đặt hàng trên PI (4.3), yêu cầu sản phẩm / bao bì của khách (2.3) ── */

const attachedFile = {
  fileName: z.string().trim().min(1).max(200),
  fileSize: z.number().int().positive().max(10 * 1024 * 1024),
  mimeType: z.string().trim().min(1).max(120),
};

/**
 * Dòng giữ lại chỉ cần `id` (khoá R2 không bao giờ gửi về trình duyệt); dòng MỚI phải có `key` đúng thư mục vừa tải lên.
 */
const keyFor = (pattern: RegExp) =>
  <T extends { id?: string; key?: string }>(v: T, ctx: z.RefinementCtx) => {
    if (!v.id && !(v.key && pattern.test(v.key))) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "bad_key", path: ["key"] });
  };
const PI_ATTACHMENT_KEY = /^pi-attachments\/[0-9a-f-]{36}\.[a-z0-9]{2,5}$/;
const REQUIREMENT_PHOTO_KEY = /^customer-requirements\/[0-9a-f-]{36}\.(png|jpg|webp|gif)$/;

/** PUT /proforma-invoices/:id/attachments — thay CẢ danh sách (giữ dòng có `id`, thêm dòng mới theo `key`, bỏ dòng vắng). */
export const piAttachmentsSchema = z.object({
  attachments: z.array(z.object({
    id: z.string().max(64).optional(),
    key: z.string().max(200).optional(),
    kind: z.enum(["image", "document"]),
    ...attachedFile,
  }).strict().superRefine(keyFor(PI_ATTACHMENT_KEY))).max(30),
});
export type PiAttachmentsInput = z.infer<typeof piAttachmentsSchema>;

export const CUSTOMER_REQUIREMENT_CATEGORY_KEYS = [
  "shippingMarks", "packagingDimensions", "productLogo", "packingQuantities", "packagingBags", "outerCartons", "other",
] as const;
export type CustomerRequirementCategory = (typeof CUSTOMER_REQUIREMENT_CATEGORY_KEYS)[number];

/** PUT /customers/:id/requirements — thay CẢ bộ mục yêu cầu của khách, mỗi mục kèm ảnh của nó. */
export const customerRequirementsSchema = z.object({
  entries: z.array(z.object({
    id: z.string().max(64).optional(),
    category: z.enum(CUSTOMER_REQUIREMENT_CATEGORY_KEYS),
    title: z.string().trim().max(200).optional(),
    description: z.string().max(4000).optional(),
    photos: z.array(z.object({
      id: z.string().max(64).optional(),
      key: z.string().max(200).optional(),
      ...attachedFile,
    }).strict().superRefine(keyFor(REQUIREMENT_PHOTO_KEY))).max(20),
  }).strict()).max(50),
});
export type CustomerRequirementsInput = z.infer<typeof customerRequirementsSchema>;
export type CustomerUpdateInput = z.infer<typeof customerUpdateSchema>;

/** GET /customers. `sort` is inherited from paginationSchema and is honoured by
 *  the repository (companyName | createdAt, optional `-` prefix for desc). */
export const customerListQuerySchema = paginationSchema.extend({
  classification: z.enum(["vietnam", "china"]).optional(),
  /** "none" = khách chưa có người theo đơn — để quản trị viên gán người trước khi bật "chỉ khách mình theo đơn" (29/09/2026). */
  followers: z.enum(["none"]).optional(),
  /**
   * "private" = chỉ khách riêng tư (yêu cầu v1.8 mục 2.1, 11/10/2026). Đứng RIÊNG, chồng được lên Việt Nam / Trung Quốc: khách
   * riêng tư vẫn thuộc đúng một công ty. Người không phải quản trị viên vốn không nhận được khách riêng tư nào nên lọc ra rỗng.
   */
  privacy: z.enum(["private"]).optional(),
});
export type CustomerListQuery = z.infer<typeof customerListQuerySchema>;

export const userCreateSchema = z.object({
  email: z.string().trim().email().max(254),
  displayName: z.string().trim().min(1).max(120),
  role: z.enum(ROLES),
  password: z.string().min(8).max(200),
});
export type UserCreateInput = z.infer<typeof userCreateSchema>;

export const userUpdateSchema = z.object({
  displayName: z.string().trim().min(1).max(120).optional(),
  role: z.enum(ROLES).optional(),
  status: z.enum(["active", "disabled"]).optional(),
});
export type UserUpdateInput = z.infer<typeof userUpdateSchema>;

/**
 * Bảng quyền của một tài khoản (mục 10 họp 12/09/2026).
 *
 * Gửi TOÀN BỘ bảng mỗi lần lưu chứ không gửi phần thay đổi: quyền là thứ người
 * quản trị phải nhìn thấy trọn vẹn trước khi bấm lưu, và một bản vá từng phần
 * thì hai người sửa cùng lúc sẽ ra một tập quyền mà không ai cố ý tạo ra.
 */
export const userAccessUpdateSchema = z.object({
  mode: z.enum(["role", "custom"]),
  customerScope: z.enum(["all", "assigned"]),
  canViewCost: z.boolean(),
  grants: z.record(z.string().max(64), z.array(z.enum(["view", "create", "update", "delete", "approve"])).max(5)),
  customerIds: z.array(z.string().max(64)).max(2000),
  /** Khu vực dữ liệu (29/09/2026) — bắt buộc như mọi phần khác của bảng: thiếu thì không được hiểu ngầm là "cả hai". */
  region: z.enum(["all", "vietnam", "china"]),
  /**
   * Nhà cung cấp RIÊNG TƯ người này được thấy (0097) — cùng bảng với ô Người theo dõi trên hồ sơ nhà cung cấp. Vắng = để nguyên
   * (trình duyệt còn chạy bản cũ lưu quyền không được xoá mất danh sách này). Chỉ thay phần nhà cung cấp riêng tư: người theo dõi
   * nhà cung cấp thường giữ nguyên.
   */
  supplierIds: z.array(z.string().max(64)).max(500).optional(),
});
export type UserAccessUpdateInput = z.infer<typeof userAccessUpdateSchema>;

/** GET /users/options — `forRegion`: chỉ người thấy được khách của khu vực đó (chọn người theo đơn, 29/09/2026). */
/** GET /products/last-sales — lần mua gần nhất của khách cho các sản phẩm đang hiện trong ô chọn (tối đa một trang). */
export const lastSalesQuerySchema = z.object({
  customerId: z.string().min(1).max(64),
  productIds: z
    .string()
    .max(4000)
    .transform((v) => [...new Set(v.split(",").map((x) => x.trim()).filter(Boolean))])
    .pipe(z.array(z.string().max(64)).min(1).max(50)),
  /** PI đang sửa — không tự tính làm "lần mua trước" của chính nó. */
  excludePiId: z.string().max(64).optional(),
});
export type LastSalesQuery = z.infer<typeof lastSalesQuerySchema>;

export const userOptionsQuerySchema = z.object({
  forRegion: z.enum(["vietnam", "china"]).optional(),
  /**
   * Chỉ người được LẬP hoặc SỬA ở menu này (yêu cầu v1.8 mục 4.2, 11/10/2026): ô "Người phụ trách" của PI lấy nhân viên đang hoạt
   * động có quyền với PI, thay danh sách gõ tay trong Cài đặt vốn chỉ còn hai tên. Tính ở máy chủ mỗi lần đọc, nên thêm người,
   * khoá tài khoản hay đổi ô tick là danh sách đổi theo.
   */
  editorsOf: z.enum(["proformaInvoices"]).optional(),
});

const P_PROCESS_ATTACHMENT_KEY = /^p-process\/[0-9a-f-]{36}\.[a-z0-9]{2,5}$/;

/** PUT /p-process/attachments — thay CẢ danh sách ảnh của MỘT ô (PI × nấc), như bằng chứng đặt hàng trên PI. */
export const pProcessAttachmentsSchema = z.object({
  piId: z.string().min(1).max(64),
  step: z.string().refine((s) => P_PROCESS_PHOTO_SLOTS.includes(s), { message: "bad_step" }),
  attachments: z.array(z.object({
    id: z.string().max(64).optional(),
    key: z.string().max(200).optional(),
    kind: z.enum(["image", "document"]),
    ...attachedFile,
  }).strict().superRefine(keyFor(P_PROCESS_ATTACHMENT_KEY))).max(30),
});
export type PProcessAttachmentsInput = z.infer<typeof pProcessAttachmentsSchema>;

/** Đánh dấu tay một bước P-Process (chỉ những bước hệ chưa có chứng từ). */
export const pProcessMarkSchema = z.object({
  piId: z.string().min(1).max(64),
  /** Rỗng/vắng = đánh dấu ở mức PI, khi chưa mở lệnh sản xuất nào. */
  moId: z.string().max(64).nullish(),
  step: z.string().min(1).max(64),
  note: z.string().max(2000).nullish(),
  /** Khoá tệp R2 của bản scan. */
  documentKey: z.string().max(512).nullish(),
});
export type PProcessMarkInput = z.infer<typeof pProcessMarkSchema>;

// 8-char floor kept deliberately: legacy allowed 6 — do not weaken it.
export const userPasswordResetSchema = z.object({
  password: z.string().min(8).max(200),
});
export type UserPasswordResetInput = z.infer<typeof userPasswordResetSchema>;

/**
 * Mở cổng cho một nhà cung cấp trong MỘT thao tác: mã đăng nhập + mật khẩu
 * ban đầu. Mã đi kèm ở đây (chứ không chỉ ở biểu mẫu nhà cung cấp) để admin
 * không phải lưu hồ sơ, quay lại trang sửa, rồi mới tạo được tài khoản.
 */
export const supplierPortalOpenSchema = z.object({
  loginId: z.string().trim().min(1).max(40),
  password: z.string().min(8).max(200),
});
export type SupplierPortalOpenInput = z.infer<typeof supplierPortalOpenSchema>;

/** Đổi mã đăng nhập và/hoặc khoá-mở tài khoản cổng. Phải có ít nhất một trường. */
export const supplierPortalUpdateSchema = z
  .object({
    loginId: z.string().trim().min(1).max(40).optional(),
    status: z.enum(["active", "disabled"]).optional(),
  })
  .refine((v) => v.loginId !== undefined || v.status !== undefined, { message: "Nothing to update" });
export type SupplierPortalUpdateInput = z.infer<typeof supplierPortalUpdateSchema>;

export const supplierContactSchema = z.object({
  name: z.string().min(1).max(120),
  phone1: phone(60),
  phone2: phone(60),
  email: z.string().email().max(160).optional().or(z.literal("")),
  wechat: z.string().max(80).optional(),
  specialization: z.string().max(120).optional(),
  sortOrder: z.number().int().min(0).optional(),
  /** Liên hệ riêng tư (0097): chỉ quản trị viên đặt được, chỉ quản trị viên thấy. */
  isPrivate: z.boolean().optional(),
});
export type SupplierContactInput = z.infer<typeof supplierContactSchema>;

export const supplierCreateSchema = z.object({
  companyName: z.string().trim().min(2).max(200),
  /**
   * BẮT BUỘC: địa chỉ nhà cung cấp được in ra Đơn đặt hàng gửi cho họ.
   * Thiếu thì chứng từ ra khỏi hệ thống với một ô trống. 9/9 NCC trên
   * production đều đã có địa chỉ.
   */
  address: z.string().trim().min(1).max(500),
  phone: phone(60),
  email: z.string().email().max(160).optional().or(z.literal("")),
  /**
   * Ít nhất MỘT người liên hệ.
   *
   * Bản in Sample Order và Purchase Order lấy người liên hệ đầu tiên của nhà
   * cung cấp; không có ai thì chứng từ gửi đi có ô liên hệ trống và bên nhận
   * không biết hỏi ai. Trên production 9/9 nhà cung cấp đều đã có liên hệ, nên
   * quy tắc này không khoá bản ghi nào đang tồn tại.
   */
  contacts: z.array(supplierContactSchema).min(1).max(15),
  /**
   * Mã đăng nhập cổng NCC — khách chốt 15/09/2026.
   *
   * Bỏ trống thì nhà cung cấp chưa mở cổng. Chuỗi rỗng nghĩa là ĐÓNG cổng lại.
   * Quy tắc định dạng và lý do không đánh số tuần tự nằm ở `portalLogin.ts`;
   * kiểm TRÙNG phải hỏi cơ sở dữ liệu nên nằm ở tầng nghiệp vụ.
   */
  portalLoginId: z.string().trim().max(40).optional(),
  /**
   * Đội dùng nhà cung cấp này (29/09/2026). Vắng: bản tạo lấy khu vực của người tạo (người thấy cả hai khu vực thì để chưa phân
   * loại), bản sửa để nguyên. `null` = chưa phân loại, cả hai đội thấy. Tài khoản một khu vực chỉ tạo được nhà cung cấp của khu
   * vực mình và không đổi được khu vực của nhà cung cấp đã có — đổi là giấu đơn mua của đội kia đi.
   */
  classification: z.enum(["vietnam", "china"]).nullable().optional(),
  /**
   * Nhà cung cấp riêng tư (0097): chỉ quản trị viên và người theo dõi thấy hồ sơ. Chỉ quản trị viên đặt / bỏ; người khác gửi `true` →
   * 403 `private_admin_only`.
   */
  isPrivate: z.boolean().optional(),
  /**
   * Người theo dõi nhà cung cấp (0097) — cũng là người được thấy nhà cung cấp riêng tư. Vắng = để nguyên. Chỉ quản trị viên sửa.
   * ≤ 20 người như người theo đơn của khách (4 cột × 20 = 80 tham số, vừa trần 100 của D1).
   */
  followerUserIds: z.array(z.string().min(1).max(64)).max(20).optional(),
});
export type SupplierCreateInput = z.infer<typeof supplierCreateSchema>;

export const supplierUpdateSchema = supplierCreateSchema.partial();
export type SupplierUpdateInput = z.infer<typeof supplierUpdateSchema>;

/** GET /suppliers — lọc theo đội; "unclassified" = nhà cung cấp chưa chọn đội. */
export const supplierListQuerySchema = paginationSchema.extend({
  classification: z.enum(["vietnam", "china", "unclassified"]).optional(),
});
export type SupplierListQuery = z.infer<typeof supplierListQuerySchema>;

/* Settings. businessOperations is intentionally extensible: the Settings
 * registry can gain more option lists without a database migration. */
const settingsOptionValue = z.string().trim().min(1).max(120);
const settingsOptionList = z.array(settingsOptionValue).max(200);

export const settingsUpdateSchema = z.object({
  expectedRevision:z.number().int().nonnegative().optional(),
  companyInfo: z.object({
    name: z.string().trim().min(1).max(200).optional(),
    address: z.string().max(500).optional(),
    phone: phone(60),
    email: z.union([z.string().trim().email().max(160),z.literal("")]).optional(),
    taxId: z.string().max(80).optional(),
  }).strict().optional(),
  businessOperations: z.object({
    optionLists: z.record(z.string().min(1).max(80), settingsOptionList).optional(),
    /** Tên tiếng Hoa theo danh mục: { productTypes: { "Button": "钮扣" } } — chỉ là nhãn, giá trị lưu vẫn là chữ tiếng Anh. */
    optionLabelsZh: z.record(
      z.enum(ZH_LABEL_LISTS),
      z.record(settingsOptionValue, z.string().trim().max(120)),
    ).optional(),
    /**
     * Tỷ giá quy đổi đơn giá sản phẩm (yêu cầu v1.8 mục 6.3): cả bộ sáu cặp mỗi lần lưu, "1 base = rate quote". `rate` rỗng = bỏ tỷ giá
     * của cặp đó (quy đổi cặp đó sẽ bị chặn cho tới khi đặt lại).
     */
    fxRates: z.array(z.object({
      base: z.enum(FX_CURRENCIES),
      quote: z.enum(FX_CURRENCIES),
      rate: z.string().trim().max(24).refine((v) => v === "" || normalizeRate(v) !== null, "invalid_rate"),
    }).strict().refine((r) => FX_PAIRS.some(([b, q]) => b === r.base && q === r.quote), "invalid_pair")).max(FX_PAIRS.length).optional(),
  }).passthrough().optional(),
  system: z.object({
    defaultCurrency: z.string().trim().min(1).max(8).optional(),
    /** Tiền tệ mặc định theo công ty (yêu cầu v1.8 mục 6.2): Trung Quốc RMB, Việt Nam USD — PI và sản phẩm mới lấy theo đây. */
    defaultCurrencyByRegion: z.object({
      vietnam: z.string().trim().min(1).max(8).optional(),
      china: z.string().trim().min(1).max(8).optional(),
    }).strict().optional(),
    /** Đơn vị mặc định cho SẢN PHẨM MỚI — hệ cũ có "giá trị mặc định" này. */
    defaultProductUnit: z.string().trim().min(1).max(32).optional(),
    language: z.string().trim().min(1).max(20).optional(),
  }).strict().optional(),
  labelPrinter: z.object({
    presetKey: z.string().trim().min(1).max(40).optional(),
    widthMm: z.number().int().min(20).max(100).optional(),
    heightMm: z.number().int().min(20).max(100).optional(),
    rotate: z.boolean().optional(),
  }).strict().optional(),
}).strict();
export type SettingsUpdateInput = z.infer<typeof settingsUpdateSchema>;

/* Products. Money fields are integer minor units (the client converts major→minor). */
const approvalEnum = z.enum(["Pending", "Approved", "Rejected", "Under Review"]);
const minorMoney = z.number().int().nonnegative();

/**
 * Columns the product list may be sorted by. An allow-list, not a free string:
 * the value reaches an ORDER BY, so anything unlisted must be rejected here
 * rather than sanitised further down.
 *
 * `costUnitPrice` is listed but is NOT free to use — the service drops it for
 * roles without `supplier_cost:view`. Sort order leaks the very thing the
 * column redaction hides: page through a cost-sorted catalog and you have the
 * suppliers' price ranking without ever seeing a number.
 */
export const PRODUCT_SORT_KEYS = [
  "productCode", "subRef", "customerRef", "description", "type", "brandName",
  "costUnitPrice", "sellingPrice", "lowestPrice", "approvalStatus",
  "stockQuantity", "createdAt",
] as const;
export type ProductSortKey = (typeof PRODUCT_SORT_KEYS)[number];

/**
 * Kỳ báo cáo cho hai màn Phân tích và Lợi nhuận, epoch ms.
 *
 * Bỏ trống thì máy chủ dùng 12 tháng gần nhất — đúng mặc định của hệ thống cũ.
 * `from > to` bị từ chối ở đây thay vì trả về bảng rỗng khó hiểu.
 */
/**
 * Kho lưu trữ bản ghi đã xoá.
 *
 * Trước đây API cắt cứng ở 200 dòng và phân trang chạy phía trình duyệt, nên
 * bản ghi cũ hơn 200 cái gần nhất là KHÔNG THỂ tới được — đúng những thứ người
 * ta cần khi truy lại chuyện cũ.
 */
export const deletedRecordsQuerySchema = paginationSchema.extend({
  /** Lọc theo bảng gốc, ví dụ "proforma_invoices". */
  table: z.string().max(60).optional(),
});
export type DeletedRecordsQuery = z.infer<typeof deletedRecordsQuerySchema>;

/**
 * Thân yêu cầu khôi phục — chỉ sản phẩm dùng tới (khách chốt 19/10/2026).
 *
 * Bản đã xoá nhả chỗ mã + Sub Ref, nên lúc khôi phục cặp đó có thể đã thuộc
 * một sản phẩm khác. Khi đó người dùng nhập mã / Sub Ref mới và khôi phục luôn
 * với giá trị ấy. Không gửi gì = khôi phục nguyên trạng; `subRef: null` hoặc
 * chuỗi rỗng = bỏ trống Sub Ref.
 */
export const deletedRecordRestoreSchema = z.object({
  productCode: z.string().trim().min(1).max(60).optional(),
  subRef: z.string().trim().max(120).nullable().optional(),
}).strict();
export type DeletedRecordRestoreInput = z.infer<typeof deletedRecordRestoreSchema>;

/** Danh sách đơn mua hàng: tìm kiếm + lọc trạng thái/ưu tiên. */
export const purchaseOrderListQuerySchema = paginationSchema.extend({
  supplierId: z.string().optional(),
  from: z.coerce.number().int().optional(),
  to: z.coerce.number().int().optional(),
  status: z.string().max(40).optional(),
  priority: z.string().max(40).optional(),
});
export type PurchaseOrderListQuery = z.infer<typeof purchaseOrderListQuerySchema>;

export const insightQuerySchema = z.object({
  from: z.coerce.number().int().optional(),
  to: z.coerce.number().int().optional(),
  /**
   * Xem riêng một đội (Roary 29/09/2026: tài khoản thấy cả hai công ty thì phân tích bán hàng phải tách được từng công ty). Chỉ
   * THU HẸP: tài khoản đã bị giới hạn một khu vực chọn khu vực kia thì không thấy gì, không bao giờ thấy thêm.
   */
  region: z.enum(["vietnam", "china"]).optional(),
}).refine((q) => q.from == null || q.to == null || q.from <= q.to, {
  message: "from must be on or before to",
  path: ["from"],
});
export type InsightQuery = z.infer<typeof insightQuerySchema>;

export const productListQuerySchema = paginationSchema.extend({
  type: z.string().optional(),
  brand: z.string().optional(),
  approvalStatus: approvalEnum.optional(),
  // Server-side because the catalog (1300+ rows) cannot fit one 200-row page.
  supplierId: z.string().optional(),
  color: z.string().trim().optional(),
  size: z.string().trim().optional(),
  // Selling price, in integer minor units like every other money field here.
  // Deliberately NOT cost: a min/max probe on cost is a binary search that
  // recovers a redacted number for a role that may not see it.
  minPrice: z.coerce.number().int().nonnegative().optional(),
  maxPrice: z.coerce.number().int().nonnegative().optional(),
  minStock: z.coerce.number().int().optional(),
  maxStock: z.coerce.number().int().optional(),
  // Epoch ms. The client converts a picked date to local start/end of day, so
  // "created on the 31st" means the user's 31st and not UTC's.
  createdFrom: z.coerce.number().int().optional(),
  createdTo: z.coerce.number().int().optional(),
  sort: z.enum(PRODUCT_SORT_KEYS).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
});
export type ProductListQuery = z.infer<typeof productListQuerySchema>;

export const productCreateSchema = z.object({
  productCode: z.string().max(60).optional(), // omit → auto-numbered (NON-unique)
  /** Bản tiếng Anh. Tên cột giữ nguyên `description` vì dữ liệu cũ nằm ở đó. */
  description: z.string().max(500).optional(),
  descriptionZh: z.string().max(500).optional(),
  /** Bản nào chép vào dòng hàng khi chưa biết chứng từ dùng tiếng gì, và bản nào in lên tem. */
  descriptionPrimary: z.enum(DESCRIPTION_LANGS).optional(),
  type: z.string().max(120).optional(),
  brandName: z.string().max(120).optional(),
  color: z.string().max(120).optional(),
  colorZh: z.string().max(120).optional(),
  size: z.string().max(60).optional(),
  unit: z.string().max(40).optional(),
  subRef: z.string().max(120).optional(),
  customerRef: z.string().max(120).optional(),
  supplierProductRef: z.string().max(120).optional(),
  // Serialized JSON string ({length,width,diameter,…}); the service writes it
  // straight into the products.wire_size json column.
  wireSize: z.string().max(2000).optional(),
  /** Thông tin vật liệu (Roary 09/10/2026). Rộng / dày: số không âm, đơn vị trong MATERIAL_SIZE_UNITS. */
  materialType: z.string().max(120).optional(),
  materialWidth: z.number().nonnegative().nullable().optional(),
  materialWidthUnit: z.enum(MATERIAL_SIZE_UNITS).optional(),
  materialThickness: z.number().nonnegative().nullable().optional(),
  materialThicknessUnit: z.enum(MATERIAL_SIZE_UNITS).optional(),
  materialHardness: z.string().max(120).optional(),
  // NOT .int() — production holds fractional values such as 3.3 g/100.
  weightPer100: z.number().nonnegative().nullable().optional(),
  weightUnit: z.string().max(16).optional(),
  supplierId: z.string().optional(),
  supplierName: z.string().max(200).optional(),
  /** Ngưỡng đặt lại hàng. Để trống = không cảnh báo tồn thấp cho sản phẩm này. */
  reorderPoint: z.number().int().nonnegative().nullable().optional(),
  moq: z.number().int().nonnegative().nullable().optional(),
  mcq: z.number().int().nonnegative().nullable().optional(),
  msq: z.number().int().nonnegative().nullable().optional(),
  stockQuantity: z.number().int().optional(),
  approvalStatus: approvalEnum.default("Pending"),
  currency: z.string().max(8).default("USD"),
  costUnitPrice: minorMoney.nullable().optional(),
  sellingPrice: minorMoney.nullable().optional(),
  lowestPrice: minorMoney.nullable().optional(),
  dtdPrice: minorMoney.nullable().optional(),
  exwPrice: minorMoney.nullable().optional(),
  underMoqPrice: minorMoney.nullable().optional(),
  upCharge: minorMoney.nullable().optional(),
  remarks: z.string().max(2000).optional(),
  storage: z.string().max(120).optional(),
  location: z.string().max(120).optional(),
  moldRef: z.string().max(120).optional(),
  moldLocation: z.string().max(120).optional(),
  sampleLeadTime: z.string().max(80).optional(),
  bulkLeadTime: z.string().max(80).optional(),
  // Image fields are written by the (later) R2 upload flow; accepted here so the
  // upload module never silently drops them.
  imageUrl: z.string().optional(),
  imageKey: z.string().optional(),
  approvalImageUrl: z.string().optional(),
  approvalImageKey: z.string().optional(),
});
export type ProductCreateInput = z.infer<typeof productCreateSchema>;

export const productUpdateSchema = productCreateSchema.partial();
export type ProductUpdateInput = z.infer<typeof productUpdateSchema>;

// Set a price for a product. customerId omitted/null = the list price.
// No effectiveFrom field: backdating is disallowed — the server stamps "now".
export const setPriceSchema = z.object({
  customerId: z.string().min(1).optional(),
  price: minorMoney,
  currency: z.string().max(8).optional(),
});
export type SetPriceInput = z.infer<typeof setPriceSchema>;

/* ───────────────────────── M3 — Sales documents ───────────────────── */
const opt = (n = 200) => z.string().max(n).optional();

/**
 * Công ty thu tiền. `beneficiary` là thứ duy nhất bắt buộc — số tài khoản có
 * thể bổ sung sau, nhưng một dòng không tên thì không chọn được trong dropdown.
 */
export const companyUpsertSchema = z.object({
  expectedRevision:z.number().int().nonnegative().optional(),
  active:z.boolean().optional(),
  beneficiary: z.string().trim().min(1).max(200),
  beneficiaryBank: opt(200),
  bankAddress: opt(500),
  accountNumberHkd: opt(80),
  accountNumberUsd: opt(80),
  accountNumberRmb: opt(80),
  swiftCode: opt(40),
  logoUrl: opt(500),
  logoKey: opt(300),
  /** Đặt làm mặc định. Máy chủ tự bỏ cờ ở các dòng khác — đúng MỘT mặc định. */
  isDefault: z.boolean().optional(),
});
export type CompanyUpsertInput = z.infer<typeof companyUpsertSchema>;
const qty = z.number().int().nonnegative().default(0);

export const unitSelectionSchema = z.object({productId:z.string().min(1),baseUnit:z.string().trim().min(1),profileId:z.string().nullable(),profileVersion:z.string().nullable()});
export type UnitSelection=z.infer<typeof unitSelectionSchema>;

export const docLineSchema = z.object({
  unitSelection:unitSelectionSchema.optional(),
  productId: z.string().optional(),
  productCode: opt(80),
  description: opt(500),
  descriptionZh: opt(500),
  color: opt(120),
  size: opt(60),
  unit: opt(40),
  quantity: qty,
  unitPrice: minorMoney.default(0),
  remark: opt(500),
  /**
   * Supplier material batch, typed by hand at goods receipt and inherited down
   * the chain. Belongs to the LINE: the customer's own production order 98230
   * carries two lots across four lines. Only collected when the parent PI has
   * p_process set — the UI hides the field otherwise, and an empty value here
   * is always legitimate.
   */
  lotNumber: opt(60),
});
export type DocLineInput = z.infer<typeof docLineSchema>;

/* PI lines carry more than the shared shape. Extended rather than widening
 * docLineSchema, which quotations/POs/packing lists also consume. */
export const piLineSchema = docLineSchema.extend({
  quantity: z.number().int().positive(),
  unitPrice: minorMoney,
  /**
   * Id của chính dòng hàng, khi đang sửa một PI có sẵn.
   *
   * Cập nhật dựng lại toàn bộ pi_items, và trước đây sinh UUID mới mỗi lần —
   * nên mọi phiếu đóng gói trỏ tới dòng cũ qua `originalItemId` lập tức treo:
   * phiếu không lưu lại được nữa (over_packed) và bảng "còn lại" hiện sai.
   * Gửi id cũ về thì dòng giữ nguyên danh tính.
   */
  id: z.string().max(40).optional(),
  customerStyleNo: opt(120),
  subRef: opt(120),
  moq: z.number().int().nonnegative().optional(),
  mcq: z.number().int().nonnegative().optional(),
  msq: z.number().int().nonnegative().optional(),
  dtdPrice: minorMoney.optional(),
  exwPrice: minorMoney.optional(),
  underMoqPrice: minorMoney.optional(),
  /**
   * Đơn giá dòng này vừa được QUY ĐỔI tiền tệ trong hộp Thêm sản phẩm (yêu cầu v1.8 mục 6.4). Máy chủ kiểm lại: tỷ giá phải đúng tỷ
   * giá đang đặt cho cặp đó, tiền đích là tiền của PI, và `unitPrice` phải đúng bằng kết quả quy đổi — không khớp là 422.
   */
  fxConversion: z.object({
    sourceCurrency: z.enum(FX_CURRENCIES),
    sourceUnitPrice: z.number().int().nonnegative(),
    base: z.enum(FX_CURRENCIES),
    quote: z.enum(FX_CURRENCIES),
    rate: z.string().trim().max(24),
  }).strict().optional(),
});
export type PiLineInput = z.infer<typeof piLineSchema>;

export const proformaInvoiceCreateSchema = z.object({
  /** Chứng từ này in bằng tiếng gì; quyết định lấy bản mô tả nào của từng dòng. */
  printLanguage: z.enum(DESCRIPTION_LANGS).optional(),
  /**
   * Chỉ quản trị viên thấy chứng từ này và mọi chứng từ phát sinh từ nó.
   * Đặt lúc TẠO; sửa về sau không gỡ được (trigger 0070).
   */
  adminOnly: z.boolean().optional(),
  /**
   * Red PI (Roary 04/11/2026, mục 9): số theo dãy R riêng, tách khỏi PI thường ở mọi màn bán hàng và báo cáo. Đặt lúc TẠO, không đổi
   * về sau (trigger 0101). Cần ô Tạo Red PI.
   */
  isRed: z.boolean().optional(),
  date: z.number().optional(),
  billTo: z.string().optional(),
  /**
   * Omit on create and the service copies customers.p_process_default. Send it
   * explicitly to override for this one document — the customer-level tick is a
   * default, never the authority.
   */
  pProcess: z.boolean().optional(),
  shipTo: opt(2000),
  notifyParty: z.string().optional(),
  notifyPartyContact: opt(2000),
  consignee: z.string().optional(),
  consigneeContact: opt(2000),
  customerPoNo: opt(120),
  customerDept: opt(200),
  deliveryDate: z.number().nullable().optional(),
  shippingMode: opt(120),
  portOfLoading: opt(120),
  countryOfOrigin: opt(120),
  dnNo: opt(120),
  paymentTerms: opt(120),
  currency: z.string().max(8).default("USD"),
  respBy: opt(120),
  paymentCompanyId: z.string().optional(),
  shippingMarks: opt(500),
  remarks: opt(2000),
  // Optional so existing callers keep compiling; the service coalesces to 0.
  upcharge: minorMoney.optional(),
  // Percent × 100, matching proforma_invoices.discount. The server must recompute
  // totalAmount = round2(round4(Σ line amounts) + upcharge) − round2(that × discount/10000).
  discount: z.number().int().min(0).max(10000).optional(),
  items: z.array(piLineSchema).default([]),
});
export type ProformaInvoiceCreateInput = z.infer<typeof proformaInvoiceCreateSchema>;
/** Bậc giá gửi lên: mốc số lượng nguyên dương, giá theo đơn vị nhỏ nhất. */
export const priceTiersSchema = z.object({
  tiers: z.array(z.object({
    fromQuantity: z.number().int().positive(),
    price: z.number().int().nonnegative(),
  })).max(20),
});
export type PriceTiersInput = z.infer<typeof priceTiersSchema>;

/** Tick "chỉ quản trị viên" — chỉ nhận lúc TẠO, không có đường gỡ về sau. */
export const proformaInvoiceUpdateSchema = proformaInvoiceCreateSchema.partial().extend({
  /**
   * Lý do sửa một PI ĐÃ XUẤT HÓA ĐƠN.
   *
   * Khách cần đổi giá trên PI rồi kéo lại vào hóa đơn (giữ nguyên số hóa đơn).
   * Cho sửa thì phải để lại vết: không có lý do thì máy chủ vẫn từ chối như cũ.
   */
  amendReason: z.string().trim().min(5).max(1000).optional(),
});
export type ProformaInvoiceUpdateInput = z.infer<typeof proformaInvoiceUpdateSchema>;

export const piPaymentSchema = z.object({
  requestId: z.string().uuid().optional(),
  date: z.number(),
  /**
   * PHẢI lớn hơn 0. Một phiếu thu 0 đồng không ghi nhận việc gì đã xảy ra,
   * nhưng vẫn hiện trong lịch sử thanh toán và trong dòng hoạt động như thể có
   * — làm bẩn đúng chỗ người ta dựa vào để đối chiếu tiền. Kiểm ở máy chủ chứ
   * không chỉ ở biểu mẫu: kiểm phía trình duyệt chỉ là trang trí.
   */
  amount: minorMoney.refine((v) => v > 0, { message: "amount_must_be_positive" }),
  method: opt(60),
  note: opt(500),
});
export type PiPaymentInput = z.infer<typeof piPaymentSchema>;

/* Invoices are issued 1:1 from a PI. */
/** Một dòng PI và số lượng muốn đưa lên hóa đơn lần này. */
export const invoiceLineAllocationSchema = z.object({
  piItemId: z.string().min(1).max(150),
  quantity: z.number().int().nonnegative(),
});

export const invoiceCreateSchema = z.object({
  /** Chứng từ này in bằng tiếng gì; quyết định lấy bản mô tả nào của từng dòng. */
  printLanguage: z.enum(DESCRIPTION_LANGS).optional(),
  expectedPiUpdatedAt:z.number().int().optional(),
  piId: z.string().min(1),
  /**
   * Khóa CHỐNG TRÙNG do máy khách sinh, dùng luôn làm id hóa đơn.
   *
   * Từ 15/09/2026 một PI xuất được nhiều hóa đơn, nên không còn ngắn mạch
   * "PI này đã có hóa đơn rồi" để chặn bấm hai lần nữa. Gửi lại cùng một
   * requestId thì đụng khóa chính và trả về đúng hóa đơn đã tạo.
   */
  requestId: z.string().uuid().optional(),
  /**
   * Chọn dòng và số lượng. Bỏ trống thì lấy TOÀN BỘ phần còn xuất được —
   * giữ nguyên hành vi cũ cho mọi nơi đang gọi mà không truyền gì.
   */
  lines: z.array(invoiceLineAllocationSchema).max(200).optional(),
});
export type InvoiceCreateInput = z.infer<typeof invoiceCreateSchema>;

export const invoiceUpdateSchema = z.object({
  expectedUpdatedAt:z.number().int().optional(),
  expectedPiUpdatedAt:z.number().int().optional(),
  piId: z.string().min(1).optional(),
  date: z.number().optional(),
  customerPoNo: opt(120),
  // The bill-to/ship-to contact snapshot. Multi-line, and 17 of 199 live
  // invoices already diverge from their PI — so it has to be editable.
  shipTo: opt(2000),
});
export type InvoiceUpdateInput = z.infer<typeof invoiceUpdateSchema>;

/* Quotation lines have their own columns; docLineSchema must not grow them. */
export const quotationLineSchema = docLineSchema.extend({
  id:z.string().max(100).optional(),
  unitPrice: minorMoney,
  quantity: z.literal(1).default(1),
  subRef: opt(80),
  moq: z.number().int().nonnegative().optional(),
  mcq: z.number().int().nonnegative().optional(),
  msq: z.number().int().nonnegative().optional(),
  sampleLeadTime: opt(80),
  bulkLeadTime: opt(80),
  imageUrl: opt(500),
  /**
   * Ba mức giá chào, đơn vị nhỏ. Lưu trên CHÍNH báo giá chứ không tra danh mục
   * lúc in: báo giá là cam kết với khách, đổi giá một sản phẩm không được phép
   * sửa lại báo giá đã gửi.
   */
  dtdPrice: minorMoney.optional(),
  exwPrice: minorMoney.optional(),
  underMoqPrice: minorMoney.optional(),
});
export type QuotationLineInput = z.infer<typeof quotationLineSchema>;

export const quotationCreateSchema = z.object({
  /** Chứng từ này in bằng tiếng gì; quyết định lấy bản mô tả nào của từng dòng. */
  printLanguage: z.enum(DESCRIPTION_LANGS).optional(),
  requestId:z.string().uuid().optional(),
  expectedUpdatedAt:z.number().int().optional(),
  pricesReviewed:z.boolean().optional(),
  refreshRecipient:z.boolean().optional(),
  omitCustomer:z.boolean().optional(),
  contactId:z.string().nullable().optional(),
  validUntil:z.number().finite().nullable().optional(),
  validityTerms:z.string().max(2000).nullable().optional(),
  customerId: z.string().optional(),
  billTo: opt(200),
  issueDate: z.number().finite().nullable().optional(),
  printingDate: z.number().finite().nullable().optional(),
  preparedBy: opt(120).nullable(),
  attn: opt(120).nullable(),
  currency: z.string().trim().min(1).max(8).transform(v=>v.toUpperCase()).default("USD"),
  remarks: opt(2000).nullable(),
  items: z.array(quotationLineSchema).default([]),
});
export type QuotationCreateInput = z.infer<typeof quotationCreateSchema>;
export const quotationUpdateSchema = quotationCreateSchema.partial();
export type QuotationUpdateInput = z.infer<typeof quotationUpdateSchema>;

export const quotationActionSchema=z.object({expectedUpdatedAt:z.number().int()});
export type QuotationActionInput=z.infer<typeof quotationActionSchema>;
export const quotationListSchema=paginationSchema.extend({customerId:z.string().optional(),preparedBy:z.string().optional(),currency:z.string().optional(),status:z.enum(["Draft","Issued","Expired"]).optional(),from:z.coerce.number().finite().optional(),to:z.coerce.number().finite().optional(),archived:z.enum(["true","false"]).optional()});
export type QuotationListInput=z.infer<typeof quotationListSchema>;

/* Packing list — cartons (CTNs) each holding one or more PI lines. */
export const packingCtnItemSchema = z.object({
  id:z.string().max(80).optional(),
  originalItemId: z.string().optional(),
  itemNo: opt(80),
  subRef: opt(80),
  description: opt(500),
  size: opt(80),
  color: opt(80),
  customerPo: opt(80),
  unit: opt(40),
  quantity: z.number().int().positive(),
  // Số LOT đi theo dòng hàng suốt chuỗi chứng từ (P-Process). Client có thể gửi
  // lại; nếu không gửi, máy chủ tự lấy từ dòng PI gốc qua originalItemId —
  // dữ liệu truy xuất nguồn gốc không được để giao diện quyết định xoá.
  lotNumber: opt(60),
});
export type PackingCtnItemInput = z.infer<typeof packingCtnItemSchema>;

// .min(1) enforces the legacy "no empty CTN" rule server-side (legacy only warned).
export const packingCtnSchema = z.object({
  ctnNo: z.string().trim().min(1).max(20),
  nwKgs: z.number().finite().nonnegative().nullable(),
  gwKgs: z.number().finite().nonnegative().nullable(),
  measurementCm: opt(80),
  items: z.array(packingCtnItemSchema).min(1),
}).refine(c=>c.nwKgs===null||c.gwKgs===null||c.gwKgs>=c.nwKgs,{message:"packing_weight_invalid",path:["gwKgs"]});
export type PackingCtnInput = z.infer<typeof packingCtnSchema>;

export const packingListCreateSchema = z.object({
  requestId:z.string().uuid().optional(),
  expectedUpdatedAt:z.number().int().optional(),
  resolveCartonConflicts:z.boolean().optional(),
  piId: z.string().min(1),
  shipTo: opt(2000),
  shippingMarks: opt(2000),
  invoiceNo: opt(60),
  attention: opt(120),
  shippingMode: opt(120),
  respBy: opt(120),
  ctns: z.array(packingCtnSchema).min(1),
});
export type PackingListCreateInput = z.infer<typeof packingListCreateSchema>;

// piId is omitted, not just optional: legacy disables the PI picker in edit mode,
// so re-pointing pi_id must be refused server-side too.
export const packingListUpdateSchema = packingListCreateSchema.omit({ piId: true }).partial();
export type PackingListUpdateInput = z.infer<typeof packingListUpdateSchema>;

/* Delivery note — raised from EXACTLY ONE of a packing list or an invoice. */
export const deliveryNoteCreateSchema = z
  .object({
    refType: z.enum(["packing", "invoice"]),
    plId: z.string().optional(),
    invoiceId: z.string().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.refType === "packing" && !v.plId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["plId"], message: "plId is required when refType is packing" });
    }
    if (v.refType === "invoice" && !v.invoiceId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["invoiceId"], message: "invoiceId is required when refType is invoice" });
    }
    if (v.plId && v.invoiceId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["invoiceId"], message: "Provide either plId or invoiceId, not both" });
    }
  });
export type DeliveryNoteCreateInput = z.infer<typeof deliveryNoteCreateSchema>;

export const deliveryNoteUpdateSchema = z.object({
  expectedUpdatedAt:z.number().int().optional(),
  reason:z.string().trim().max(2000).optional(),
  correction:z.boolean().optional(),
  receiverName:opt(120),
  shipTo: opt(2000),
  deliveryStatus: z.enum(["Pending", "In Transit", "Delivered", "Cancelled"]).optional(),
  deliveryDate: z.number().nullable().optional(),
  deliveryMode: opt(80),
  vehicleNo: opt(60),
  driverName: opt(120),
  driverPhone: opt(60),
  respBy: opt(120),
  remarks: opt(2000),
  // The delivered-qty grid. The service must clamp deliveredQty <= dn_items.ordered_qty
  // and recompute delivery_notes.total_quantity server-side.
  items: z
    .array(
      z.object({
        lineNo: z.number().int().positive(),
        orderedQty:z.number().int().nonnegative().safe().optional(),
        deliveredQty: z.number().int().nonnegative(),
        remarks: opt(500),
        /**
         * Cách giao dòng này — BẮT BUỘC khi chuyển phiếu sang "Đã giao" với số giao > 0 (18/09/2026, Kho & LOT là
         * nguồn tồn duy nhất). `Stock`: xuất từ vị trí lô entryId/warehouseId/location, máy chủ ghi sổ phiếu xuất
         * bán trong cùng lần lưu. `Direct`: giao thẳng từ nhà máy / nhà cung cấp, không qua kho — ghi chú bắt buộc.
         */
        fulfillment: z.enum(["Stock", "Direct"]).optional(),
        fulfillmentNote: z.string().trim().max(500).optional(),
        entryId: z.string().trim().max(100).optional(),
        warehouseId: z.string().trim().max(150).optional(),
        location: z.string().trim().max(100).optional(),
      }),
    )
    .optional(),
});
export type DeliveryNoteUpdateInput = z.infer<typeof deliveryNoteUpdateSchema>;

/** Người có quyền Duyệt ghi nhận đã xem một dòng giao thẳng trong lúc kho có hàng (0088). */
export const deliveryDirectReviewSchema = z.object({
  lineNo: z.number().int().positive(),
  note: z.string().trim().min(5).max(500),
});
export type DeliveryDirectReviewInput = z.infer<typeof deliveryDirectReviewSchema>;

/**
 * Một vị trí lô xuất được cho một dòng phiếu giao (cùng sản phẩm, cùng đơn vị, còn hàng dùng được).
 * `available` là phần XUẤT BÁN được: tồn dùng được trừ phần đang giữ chỗ (trigger 0054 không cho phiếu xuất bán
 * lấy vào hàng đã giữ chỗ, kể cả giữ cho chính đơn đó).
 */
export interface DeliveryStockPositionDto {
  entryId: string;
  warehouseId: string;
  warehouseName: string;
  location: string;
  lotNumber: string;
  available: number;
}
/** Lựa chọn xuất kho cho từng dòng phiếu giao — cho bước xác nhận "Đã giao". */
export interface DeliveryStockOptionDto {
  lineNo: number;
  productFound: boolean;
  positions: DeliveryStockPositionDto[];
}

export const poStatusSchema = z.object({
  status: z.enum(["Draft", "Sent", "Confirmed", "Partially Delivered", "Delivered", "Cancelled"]),
});

/* po_items has sub_ref/moq/supplier_name, names its note column `remarks`
 * (not `remark`) and has no `unit` — docLineSchema cannot be reused as-is. */
export const poLineSchema = z.object({
  id:z.string().max(100).optional(),
  unitSelection:unitSelectionSchema.optional(),
  unit:z.string().max(40).nullable().optional(),
  piItemId:z.string().nullable().optional(),
  lotNumber: opt(120),
  productId: z.string().optional(),
  productCode: opt(80),
  subRef: opt(80),
  description: opt(500),
  descriptionZh: opt(500),
  color: opt(120),
  size: opt(60),
  moq: z.number().int().nonnegative().optional(),
  quantity: qty,
  unitPrice: minorMoney,
  remarks: opt(500),
  supplierName: opt(200),
});
export type PoLineInput = z.infer<typeof poLineSchema>;

// poNumber is NOT accepted from the client — it is allocated from document_counters.
export const purchaseOrderCreateSchema = z.object({
  /** Chứng từ này in bằng tiếng gì; quyết định lấy bản mô tả nào của từng dòng. */
  printLanguage: z.enum(DESCRIPTION_LANGS).optional(),
  pricesReviewed:z.boolean().optional(),
  /**
   * Số PO tự nhập, GHI ĐÈ bộ đếm.
   *
   * Bộ đếm vẫn là mặc định (quy tắc vàng 5), nhưng người mua đôi khi phải khớp
   * số PO mà nhà cung cấp hoặc khách đã ấn định. Máy chủ kiểm trùng trước khi
   * ghi — hai đơn cùng số là hai đơn không đối chiếu được.
   */
  poNumber: opt(40),
  /** Người liên hệ bên nhà cung cấp, chốt lại trên chứng từ. */
  supplierContact: opt(120).nullable(),
  supplierId: z.string().min(1).optional(),
  orderDate: z.number().optional(),
  expectedDeliveryDate: z.number().nullable().optional(),
  actualDeliveryDate: z.number().nullable().optional(),
  paymentTerms: opt(120).nullable(),
  shippingMethod: opt(120).nullable(),
  priority: opt(40).nullable(),
  status: z
    .enum(["Draft", "Sent", "Confirmed", "Partially Delivered", "Delivered", "Cancelled"])
    .default("Draft"),
  currency: z.string().max(8).default("USD"),
  shipping: minorMoney.default(0),
  taxPercentage: z.number().int().min(0).max(10000).default(0), // stored ×100
  linkedPiId: z.string().nullable().optional(),
  remarks: opt(2000).nullable(),
  items: z.array(poLineSchema).min(1),
});
export type PurchaseOrderCreateInput = z.infer<typeof purchaseOrderCreateSchema>;
export const purchaseOrderUpdateSchema = purchaseOrderCreateSchema.partial();
export type PurchaseOrderUpdateInput = z.infer<typeof purchaseOrderUpdateSchema>;

export const poFromPiSchema = z.object({
  piId: z.string().min(1),
  previewToken: z.string().optional(),
  currency: z.string().trim().min(1).max(8).optional(),
  prices: z.array(z.object({ lineId: z.string().min(1), unitPrice: minorMoney })).max(1000).optional(),
});
export type PoFromPiInput = z.infer<typeof poFromPiSchema>;

/**
 * Điều khoản mặc định của đơn mua mới — như hệ cũ (PurchaseOrder.js). Đơn mua lập tay và đơn mua lập từ PI dùng CÙNG mặc định này;
 * đơn lập từ PI KHÔNG chép điều khoản thanh toán / cách vận chuyển / ngày giao của PI: đó là thoả thuận với KHÁCH, còn đơn mua là với
 * NHÀ CUNG CẤP (anh Toàn chốt 07/10/2026). Quản trị viên đã bỏ giá trị mặc định khỏi danh sách trong Cài đặt thì lấy mục đầu tiên của
 * danh sách. Người mua sửa trên đơn nháp như thường.
 */
export const PO_DEFAULT_PAYMENT_TERMS = "T/T 30% deposit, 70% before shipment";
export const PO_DEFAULT_SHIPPING_METHOD = "Sea Freight";
export function poDefaultTerms(lists?: { paymentTerms?: readonly string[]; shippingMethods?: readonly string[] }) {
  const pick = (fallback: string, list?: readonly string[]) => {
    const values = (list ?? []).map((v) => v.trim()).filter(Boolean);
    return !values.length || values.includes(fallback) ? fallback : values[0]!;
  };
  return { paymentTerms: pick(PO_DEFAULT_PAYMENT_TERMS, lists?.paymentTerms), shippingMethod: pick(PO_DEFAULT_SHIPPING_METHOD, lists?.shippingMethods) };
}

/* M4 — Samples */
export const sampleItemSchema = z.object({
  productId: z.string().optional(),
  productNo: opt(80),
  subRef: opt(80),
  type: opt(120),
  description: opt(500),
  descriptionZh: opt(500),
  brand: opt(120),
  color: opt(120),
  size: opt(60),
  requestQuantity: z.number().int().nonnegative().default(0),
  retainedQuantity: z.number().int().nonnegative().default(0),
  remarks: opt(2000),
  // A manual-entry line has no productId, so it carries its own image.
  isManualEntry: z.boolean().optional(),
  imageUrl: z.string().optional(),
  imageKey: z.string().optional(),
  controlSampleUrl: z.string().optional(),
  controlSampleKey: z.string().optional(),
  controlSampleDescription: opt(500),
});
export type SampleItemInput = z.infer<typeof sampleItemSchema>;

export const sampleAttachmentSchema = z.object({
  type: z.enum(["image", "document"]).default("image"),
  fileName: opt(255),
  fileUrl: z.string().optional(),
  fileKey: z.string().optional(),
  fileSize: z.number().int().nonnegative().optional(),
  mimeType: opt(120),
});
export type SampleAttachmentInput = z.infer<typeof sampleAttachmentSchema>;
export const sampleOrderCreateSchema = z.object({
  /** Thông tin gửi hàng mẫu — tách trường để tra được theo số vận đơn. */
  courier: opt(120),
  trackingNo: opt(120),
  shippedAt: z.number().int().nullish(),
  /** Chứng từ này in bằng tiếng gì; quyết định lấy bản mô tả nào của từng dòng. */
  printLanguage: z.enum(DESCRIPTION_LANGS).optional(),
  orderType: z.enum(["From Supplier", "To Customer"]).default("From Supplier"),
  supplierId: z.string().nullable().optional(),
  customerId: z.string().nullable().optional(),
  orderDate: z.number().optional(),
  deliveryDate: z.number().nullable().optional(),
  teamDepartment: opt(120),
  labTestRequest: opt(500),
  remarks: opt(2000),
  // Người liên hệ được CHỐT lên đơn khi tạo (migration 0011). Cho sửa vì đối
  // tác có thể chỉ định người khác phụ trách riêng đơn mẫu này.
  contactPerson: opt(160),
  contactPhone: opt(60),
  contactEmail: opt(160),
  items: z.array(sampleItemSchema).max(50).default([]),
  // Cap 10 matches the legacy ATTACHMENT_CONFIG.maxFiles.
  // undefined = leave alone; an array = full replace (service coalesces on create).
  attachments: z.array(sampleAttachmentSchema).max(10).optional(),
});
export type SampleOrderCreateInput = z.infer<typeof sampleOrderCreateSchema>;
export const sampleOrderUpdateSchema = sampleOrderCreateSchema.partial().extend({
  status: z.enum(["Draft", "Sent", "Received", "Testing", "Approved", "Rejected", "Completed"]).optional(),
});
export type SampleOrderUpdateInput = z.infer<typeof sampleOrderUpdateSchema>;
export const sampleStatusSchema = z.object({
  status: z.enum(["Draft", "Sent", "Received", "Testing", "Approved", "Rejected", "Completed"]),
});
/**
 * Bộ lọc danh sách đơn mẫu. `status` được lọc Ở MÁY CHỦ, không phải trên trang
 * đang hiển thị — xem sampleOrdersRepo.list.
 *
 * Dùng lại enum của sampleStatusSchema để không bao giờ có hai danh sách trạng
 * thái trôi khỏi nhau.
 */
export const sampleOrderListSchema = paginationSchema.extend({
  orderType: z.enum(["From Supplier", "To Customer"]).optional(),
  partyId: z.string().optional(),
  team: z.string().max(120).optional(),
  from: z.coerce.number().int().optional(),
  to: z.coerce.number().int().optional(),
  status: sampleStatusSchema.shape.status.optional(),
});
export type SampleOrderListInput = z.infer<typeof sampleOrderListSchema>;

export const sampleCatalogCommitSchema = z.object({ previewToken: z.string().optional() });

export const sampleCardCreateSchema = z.object({
  /**
   * Chuỗi RỖNG = gỡ liên kết.
   *
   * `undefined` bị loại khỏi JSON nên máy chủ không biết người dùng vừa bấm
   * "bỏ chọn"; chỉ đổi sang bản ghi khác được, không gỡ hẳn được. Service đổi
   * chuỗi rỗng thành NULL.
   */
  productId: z.string().optional(),
  productNo: opt(80),
  /** Mã phụ — cột sub_ref đã có dữ liệu nhưng DTO/form chưa hề dùng tới. */
  subRef: opt(80),
  description: opt(500),
  type: opt(120),
  brand: opt(120),
  color: opt(120),
  size: opt(60),
  imageUrl: z.string().optional(),
  imageKey: z.string().optional(),
  customerId: z.string().optional(),
  /** Người liên hệ CHỐT lên phiếu (0013); chọn từ danh bạ của khách. */
  contactPerson: opt(160),
  contactPhone: opt(60),
  // Email đã chốt trên phiếu — 69/94 phiếu cũ có (migration 0015).
  contactEmail: opt(160),
  remarks: opt(2000),
});
export type SampleCardCreateInput = z.infer<typeof sampleCardCreateSchema>;
export const sampleCardUpdateSchema = sampleCardCreateSchema.partial();
export type SampleCardUpdateInput = z.infer<typeof sampleCardUpdateSchema>;

export const sampleCardListSchema = paginationSchema.extend({
  customerId: z.string().optional(),
  type: z.string().max(120).optional(),
  brand: z.string().max(120).optional(),
  from: z.coerce.number().int().optional(),
  to: z.coerce.number().int().optional(),
});
export type SampleCardListInput = z.infer<typeof sampleCardListSchema>;

export const piListSchema = paginationSchema.extend({
  invoiceAvailable:z.enum(["true","false"]).optional(),keepPiId:z.string().max(100).optional(),status:z.enum(["all","unpaid","partial","paid"]).optional(),customerId:z.string().optional(),respBy:z.string().optional(),from:z.coerce.number().optional(),to:z.coerce.number().optional()});
export type PiListInput=z.infer<typeof piListSchema>;

export const piVoidPaymentSchema=z.object({reason:z.string().trim().min(1).max(500)});

export const invoiceListSchema=paginationSchema.extend({customerId:z.string().optional(),respBy:z.string().optional(),from:z.coerce.number().finite().optional(),to:z.coerce.number().finite().optional(),archived:z.enum(["true","false"]).optional()});
export type InvoiceListInput=z.infer<typeof invoiceListSchema>;

export const packingListQuerySchema=paginationSchema.extend({piId:z.string().optional(),respBy:z.string().optional(),from:z.coerce.number().finite().optional(),to:z.coerce.number().finite().optional()});
export type PackingListQueryInput=z.infer<typeof packingListQuerySchema>;

export const deliveryShipmentSchema=z.object({
 refType:z.enum(["packing","invoice"]),sourceId:z.string().min(1),requestId:z.string().uuid(),
 expectedSourceUpdatedAt:z.number().int().optional(),shipTo:z.string().trim().min(1).max(2000),
 deliveryMode:opt(80),vehicleNo:opt(60),driverName:opt(120),driverPhone:opt(60),respBy:opt(120),remarks:opt(2000),
 items:z.array(z.object({sourceItemId:z.string().min(1),plannedQty:z.number().int().positive().safe()})).min(1)
}).refine(v=>new Set(v.items.map(i=>i.sourceItemId)).size===v.items.length,{message:"duplicate_line"});
export type DeliveryShipmentInput=z.infer<typeof deliveryShipmentSchema>;
export const deliveryListSchema=paginationSchema.extend({status:z.enum(["Pending","In Transit","Delivered","Cancelled"]).optional(),customerId:z.string().optional(),refType:z.enum(["packing","invoice"]).optional(),from:z.coerce.number().finite().optional(),to:z.coerce.number().finite().optional(),archived:z.enum(["true","false"]).optional()});
export type DeliveryListInput=z.infer<typeof deliveryListSchema>;

export const statementQuerySchema=z.object({mode:z.enum(["current","asOf"]).default("current"),asOf:z.coerce.number().finite().optional(),from:z.coerce.number().finite().optional(),customerId:z.string().optional(),currency:z.string().trim().max(8).optional(),q:z.string().trim().max(200).optional(),filter:z.enum(["all","outstanding","overdue","settled","credit","review"]).default("all"),sort:z.enum(["date","-date","due","-balance"]).default("-date"),tzOffsetMinutes:z.coerce.number().int().min(-840).max(840).default(0)}).superRefine((v,c)=>{if(v.mode==="asOf"&&v.asOf===undefined)c.addIssue({code:"custom",path:["asOf"],message:"statement_cutoff_required"});if(v.from!==undefined&&v.asOf!==undefined&&v.mode==="asOf"&&v.from>v.asOf)c.addIssue({code:"custom",path:["from"],message:"statement_range_invalid"});});
export type StatementQueryInput=z.infer<typeof statementQuerySchema>;

export const profitQuerySchema=z.object({from:z.coerce.number().finite().optional(),to:z.coerce.number().finite().optional(),customerId:z.string().optional(),currency:z.string().trim().max(8).optional(),q:z.string().trim().max(200).optional(),costStatus:z.enum(["all","po","mixed","catalog","missing"]).default("all"),profitState:z.enum(["all","profit","loss","breakeven","unknown"]).default("all"),sort:z.enum(["-margin","margin","date","-date","-revenue","cost","-marginPct"]).default("-margin"),tzOffsetMinutes:z.coerce.number().int().min(-840).max(840).default(0)}).superRefine((v,c)=>{if(v.from!==undefined&&v.to!==undefined&&v.from>v.to)c.addIssue({code:"custom",path:["from"],message:"profit_range_invalid"});});
export type ProfitQueryInput=z.infer<typeof profitQuerySchema>;

export const releasesQuerySchema=z.object({
 page:z.coerce.number().int().positive().default(1),
 pageSize:z.coerce.number().int().min(1).max(100).default(25),
 loadedId:z.string().max(100).optional(),
});
export type ReleasesQueryInput=z.infer<typeof releasesQuerySchema>;
